import { useEffect, useState } from "react";
import api from "@/lib/api";
import Tip from "@/components/Tip";
import { Activity, Clock, Send, MessageCircle, Mail, AlertTriangle, PlayCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BRAND_BLUE = "#004a99";
const BRAND_ORANGE = "#f37021";

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

function countdown(toIso) {
  if (!toIso) return "—";
  const ms = new Date(toIso) - new Date();
  if (ms <= 0) return "running…";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function CronHealthWidget() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  const load = async () => {
    try {
      const r = await api.get("/super-admin/cron/status");
      setData(r.data);
    } catch (e) {
      setData({ error: true });
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30_000); // refresh countdown every 30s
    return () => clearInterval(t);
  }, []);

  const trigger = async () => {
    setBusy(true);
    try {
      const r = await api.post("/super-admin/cron/trigger-now");
      const s = r.data.summary;
      toast.success(`Cron run · scanned ${s.scanned} · WA ${s.whatsapp_sent} · Email ${s.email_sent}`);
      await load();
    } catch { toast.error("Trigger failed"); }
    finally { setBusy(false); }
  };

  if (!data) {
    return (
      <div className="card-3d bg-white rounded-2xl p-5 border-2" style={{ borderColor: `${BRAND_BLUE}30` }} data-testid="cron-health-widget">
        <div className="text-sm text-slate-500">Loading cron health…</div>
      </div>
    );
  }
  if (data.error) {
    return (
      <div className="card-3d bg-white rounded-2xl p-5 border-2 border-red-200" data-testid="cron-health-widget">
        <div className="flex items-center gap-2 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4" /> Could not load cron status
        </div>
      </div>
    );
  }

  const running = data.scheduler_running;
  const nextJob = data.jobs?.[0];
  const next = nextJob?.next_run;
  const recent = data.recent_runs || [];
  void tick;

  return (
    <div className="card-3d bg-white rounded-2xl p-5 border-2 fade-up" style={{ borderColor: `${BRAND_BLUE}30` }} data-testid="cron-health-widget">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="icon-3d icon-float h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: `${BRAND_BLUE}15` }}>
            <Activity className="h-5 w-5" style={{ color: BRAND_BLUE }} />
          </div>
          <div>
            <h2 className="font-heading font-bold text-base flex items-center" style={{ color: BRAND_BLUE }}>
              Cron Health
              <Tip text="Daily Payment-Ledger reminder cron sends WhatsApp + Email to every clinic's pending invoices. Runs every day at 10:00 IST. Tap 'Run now' for a manual trigger." />
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Daily Payment-Ledger reminders</p>
          </div>
        </div>
        <button onClick={load} className="text-slate-400 hover:text-slate-700 transition" data-testid="cron-refresh" title="Refresh">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Status row */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-xl p-3" style={{ background: running ? `${BRAND_BLUE}10` : "#fee2e2" }} data-testid="cron-status-pill">
          <div className="flex items-center gap-1.5">
            {running ? <span className="pulse-dot" /> : <span className="h-2 w-2 rounded-full bg-red-500" />}
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: running ? BRAND_BLUE : "#b91c1c" }}>{running ? "Live" : "Stopped"}</span>
          </div>
          <div className="text-sm font-bold mt-1" style={{ color: running ? BRAND_BLUE : "#b91c1c" }}>{running ? "Scheduler running" : "Scheduler offline"}</div>
        </div>
        <div className="rounded-xl p-3" style={{ background: `${BRAND_ORANGE}12` }} data-testid="cron-next-run">
          <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: BRAND_ORANGE }}>Next run in</div>
          <div className="text-lg font-extrabold mt-0.5" style={{ color: BRAND_ORANGE }}>{countdown(next)}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">{fmtDT(next)}</div>
        </div>
        <div className="rounded-xl p-3 bg-slate-50">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Schedule</div>
          <div className="text-sm font-bold text-slate-800 mt-1 flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{String(data.config?.hour).padStart(2, "0")}:{String(data.config?.minute).padStart(2, "0")} IST</div>
          <div className="text-[10px] text-slate-500 mt-0.5">{data.config?.enabled ? "enabled" : "disabled"}</div>
        </div>
      </div>

      <Button onClick={trigger} disabled={busy} size="sm" className="w-full text-white btn-sheen mb-4" style={{ background: BRAND_BLUE }} data-testid="cron-trigger-now">
        <PlayCircle className="h-4 w-4 mr-1.5" /> {busy ? "Running…" : "Run reminders now"}
      </Button>

      {/* Recent runs */}
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
          <Send className="h-3 w-3" /> Last 5 runs
        </div>
        {recent.length === 0 ? (
          <div className="text-xs text-slate-400 py-3 text-center bg-slate-50 rounded-lg" data-testid="cron-no-runs">No runs yet — daily cron will fire automatically, or tap "Run now".</div>
        ) : (
          <div className="space-y-1.5 max-h-44 overflow-y-auto">
            {recent.slice(0, 5).map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg border" style={{ borderColor: `${BRAND_BLUE}20`, background: i === 0 ? `${BRAND_BLUE}07` : "#fff" }} data-testid={`cron-run-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-slate-500 flex-shrink-0">{fmtDT(r.ran_at)}</span>
                  {r.manual && <span className="text-[10px] uppercase tracking-wide px-1.5 rounded-full" style={{ background: `${BRAND_ORANGE}20`, color: BRAND_ORANGE }}>manual</span>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="font-semibold" style={{ color: BRAND_BLUE }}>{r.scanned} scanned</span>
                  <span className="flex items-center gap-0.5 text-emerald-700"><MessageCircle className="h-3 w-3" />{r.whatsapp_sent}</span>
                  <span className="flex items-center gap-0.5 text-blue-700"><Mail className="h-3 w-3" />{r.email_sent}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
